// client/src/screens/admin/products/textile/TextilesProductForm.shared.jsx

import React, { memo } from "react";

import { TEXTILE_SHOP_KEY } from "../../../../constants/shopMeta";
import { normalizeProductImage } from "../../../../utils/productImages";

/* -------------------------------------------------------------------------- */
/*                                  Constants                                 */
/* -------------------------------------------------------------------------- */

export const SHOP_KEY = TEXTILE_SHOP_KEY;

export const DRAFT_SCHEMA_VERSION = 4;
export const DRAFT_TTL_MS = 1000 * 60 * 60 * 24 * 14;
export const DRAFT_WRITE_DELAY_MS = 400;

export const FLAG_FIELDS = [
  "exclusive",
  "limitedEdition",
  "isVintage",
  "isCustomMade",
  "isNewArrival",
  "isEditorsPick",
  "isTrending",
  "isFeatured",
];

export const TEXTILE_VARIANT_OPTIONS = [
  { value: "metre", label: "One Metre - 1 m", unitValue: 1 },
  { value: "half", label: "Half Metre - 0.5 m", unitValue: 0.5 },
  { value: "sample", label: "Sample", unitValue: 1 },
  { value: "cut", label: "Cut (legacy, unavailable in Shop)", unitValue: 1, legacy: true },
];

export const TEXTILE_VARIANT_TYPE_SET = new Set(
  TEXTILE_VARIANT_OPTIONS.map((item) => item.value),
);

export const DEFAULT_VARIANT_TYPE = "metre";

/* -------------------------------------------------------------------------- */
/*                              Primitive helpers                             */
/* -------------------------------------------------------------------------- */

export function safeText(value = "") {
  return String(value ?? "").trim();
}

export function normalizeLower(value = "") {
  return safeText(value).toLowerCase();
}

export function normalizeSku(value = "") {
  return safeText(value).toUpperCase();
}

export function getIdValue(value) {
  if (!value) return "";
  if (typeof value === "string") return value;
  return value?._id || value?.id || "";
}

export function getProductId(product = null) {
  return getIdValue(product);
}

export function makeClientId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function hasDefinedValue(value) {
  return value !== undefined && value !== null;
}

function firstDefined(...values) {
  return values.find(hasDefinedValue);
}

function stringifyFormValue(value, fallback = "") {
  const resolved = hasDefinedValue(value) ? value : fallback;
  return hasDefinedValue(resolved) ? String(resolved) : "";
}

export function splitTokens(value = "") {
  return [
    ...new Set(
      String(value || "")
        .split(/[\n,]+/)
        .map((item) => normalizeLower(item))
        .filter(Boolean),
    ),
  ];
}

export function joinTokens(values = [], fallback = "") {
  if (Array.isArray(values) && values.length) {
    return values.map(safeText).filter(Boolean).join(", ");
  }

  return safeText(fallback);
}

export function toNumberOrZero(value) {
  const number = Number(value);
  return Number.isFinite(number) && number >= 0 ? number : 0;
}

export function toIntegerOrZero(value) {
  const number = Math.floor(Number(value));
  return Number.isFinite(number) && number >= 0 ? number : 0;
}

export function hasPositiveNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) && number > 0;
}

/* -------------------------------------------------------------------------- */
/*                              Textile helpers                               */
/* -------------------------------------------------------------------------- */

export function normalizeVariantType(value = "") {
  const next = normalizeLower(value);
  return TEXTILE_VARIANT_TYPE_SET.has(next) ? next : DEFAULT_VARIANT_TYPE;
}

export function getVariantUnitValue(variantType = "") {
  return (
    TEXTILE_VARIANT_OPTIONS.find(
      (option) => option.value === normalizeVariantType(variantType),
    )?.unitValue || 1
  );
}

/* -------------------------------------------------------------------------- */
/*                                  Images                                    */
/* -------------------------------------------------------------------------- */

export function normalizeImage(image) {
  return normalizeProductImage(image);
}

export function normalizeImageArray(images = []) {
  const raw = Array.isArray(images) ? images : images ? [images] : [];
  return raw.map(normalizeImage).filter(Boolean);
}

/* -------------------------------------------------------------------------- */
/*                                  State                                     */
/* -------------------------------------------------------------------------- */

export function buildTextileVariantRow(variant = {}) {
  const variantType = normalizeVariantType(
    variant?.variantType || variant?.type,
  );

  return {
    _clientId: variant?._clientId || makeClientId(),
    variantType,
    enabled: variantType !== "cut" && variant.enabled !== false && variant.isActive !== false,
    legacyUnitValue: variant.legacyUnitValue ?? variant.unitValue ?? 1,
    price:
      variant?.price === undefined || variant?.price === null
        ? ""
        : String(variant.price),
  };
}

export function buildFlagState(source = null, fallback = null) {
  const merged = {
    ...(fallback || {}),
    ...(source || {}),
  };

  return FLAG_FIELDS.reduce((acc, key) => {
    acc[key] = Boolean(merged?.[key]);
    return acc;
  }, {});
}

export function mergeById(items = []) {
  const map = new Map();

  for (const item of items) {
    const id = getIdValue(item);
    if (id) map.set(id, item);
  }

  return [...map.values()];
}

export function buildInitialState(product = null, draft = null) {
  const source = draft || product || {};
  const textile = source.textile || product?.textile || {};
  const madeToMeasure = source.madeToMeasure || product?.madeToMeasure || {};

  const productVariants =
    product?.isVariant && Array.isArray(product?.variants)
      ? product.variants
      : [];

  const draftVariants =
    Array.isArray(draft?.variants) && draft.variants.length
      ? draft.variants
      : null;

  const unitType = normalizeVariantType(
    firstDefined(source.unitType, textile.unitType, DEFAULT_VARIANT_TYPE),
  );

  return {
    category: getIdValue(source.category || product?.category),
    subcategory: getIdValue(
      source.subCategory ||
        source.subcategory ||
        product?.subCategory ||
        product?.subcategory,
    ),

    name: safeText(source.name),
    brand: safeText(source.brand),
    description: safeText(source.description),
    sku: normalizeSku(source.sku || product?.sku),

    isVariant: Boolean(source.isVariant),

    price: stringifyFormValue(
      firstDefined(source.price, product?.isVariant ? "" : product?.price, ""),
    ),

    unitType,

    unitValue: stringifyFormValue(
      firstDefined(source.unitValue, textile.unitValue),
      getVariantUnitValue(unitType),
    ),

    lengthInStock: stringifyFormValue(
      firstDefined(source.lengthInStock, textile.lengthInStock),
    ),

    lengthReserved: stringifyFormValue(
      firstDefined(source.lengthReserved, textile.lengthReserved),
      "0",
    ),

    sampleInStock: stringifyFormValue(
      firstDefined(source.sampleInStock, textile.sampleInStock),
    ),

    sampleReserved: stringifyFormValue(
      firstDefined(source.sampleReserved, textile.sampleReserved),
      "0",
    ),

    sampleLimit: stringifyFormValue(
      firstDefined(source.sampleLimit, textile.sampleLimit),
      "0",
    ),

    colorsText:
      source.colorsText ||
      joinTokens(source.colors, source.color) ||
      joinTokens(product?.colors, product?.color),

    materialsText:
      source.materialsText ||
      joinTokens(source.materials, source.material) ||
      joinTokens(product?.materials, product?.material),

    patternsText:
      source.patternsText ||
      joinTokens(source.patterns) ||
      joinTokens(product?.patterns),

    tagsText:
      source.tagsText || joinTokens(source.tags) || joinTokens(product?.tags),

    groupsText:
      source.groupsText ||
      joinTokens(source.groups) ||
      joinTokens(product?.groups),

    origin: safeText(source.origin || product?.origin),
    usage: safeText(source.usage || product?.usage),
    careInstructions: safeText(
      source.careInstructions || product?.careInstructions,
    ),

    madeToMeasureEligible: Boolean(madeToMeasure.eligibleAsTextile),
    enabled: source.enabled ?? (textile.enabled !== false && textile.isActive !== false),
    madeToMeasureSuitabilityNotes: safeText(madeToMeasure.suitabilityNotes),

    images: normalizeImageArray(source.images ?? product?.images),

    ...buildFlagState(source, product),

    variants: draftVariants
      ? draftVariants.map(buildTextileVariantRow)
      : productVariants.length
        ? productVariants.map(buildTextileVariantRow)
        : [
            buildTextileVariantRow({ variantType: "metre" }),
            buildTextileVariantRow({ variantType: "half" }),
            buildTextileVariantRow({ variantType: "sample" }),
          ],
  };
}

/* -------------------------------------------------------------------------- */
/*                               Draft storage                                */
/* -------------------------------------------------------------------------- */

export function getDraftKey(product = null) {
  return `textiles-product-form:${getProductId(product) || "new"}`;
}

function getLocalStorage() {
  if (typeof window === "undefined") return null;

  try {
    return window.localStorage || null;
  } catch {
    return null;
  }
}

export function readDraft(key) {
  const storage = getLocalStorage();
  if (!storage) return null;

  try {
    const raw = storage.getItem(key);
    if (!raw) return null;

    const parsed = JSON.parse(raw);

    if (parsed?.schemaVersion !== DRAFT_SCHEMA_VERSION) {
      storage.removeItem(key);
      return null;
    }

    if (!parsed?.savedAt || Date.now() - parsed.savedAt > DRAFT_TTL_MS) {
      storage.removeItem(key);
      return null;
    }

    return parsed.data || null;
  } catch {
    return null;
  }
}

export function writeDraft(key, value) {
  const storage = getLocalStorage();
  if (!storage) return;

  try {
    storage.setItem(
      key,
      JSON.stringify({
        schemaVersion: DRAFT_SCHEMA_VERSION,
        savedAt: Date.now(),
        data: value,
      }),
    );
  } catch {
    // Ignore storage quota/security errors.
  }
}

export function clearDraft(key) {
  const storage = getLocalStorage();
  if (!storage) return;

  try {
    storage.removeItem(key);
  } catch {
    // Ignore storage errors.
  }
}

/* -------------------------------------------------------------------------- */
/*                                Validation                                  */
/* -------------------------------------------------------------------------- */

export function validateForm(form) {
  const category = safeText(form.category);
  const name = safeText(form.name);
  const description = safeText(form.description);
  const sku = normalizeSku(form.sku);
  const unitType = normalizeVariantType(form.unitType);

  if (!category) return "Please select a category before saving this textile.";
  if (!name) return "Product name is required.";
  if (!description) return "Product description is required.";
  if (!sku) return "SKU is required for textile products.";

  if (!form.isVariant) {
    if (!hasPositiveNumber(form.price)) {
      return "Price must be greater than 0.";
    }

    if (!TEXTILE_VARIANT_TYPE_SET.has(unitType)) {
      return "Choose a valid textile buying option.";
    }

    if (!hasPositiveNumber(form.unitValue)) {
      return "Unit value must be greater than 0.";
    }

    if (
      toNumberOrZero(form.lengthReserved) > toNumberOrZero(form.lengthInStock)
    ) {
      return "Length reserved cannot exceed length in stock.";
    }

    if (
      toIntegerOrZero(form.sampleReserved) > toIntegerOrZero(form.sampleInStock)
    ) {
      return "Sample reserved cannot exceed sample stock.";
    }

    return "";
  }

  const variants = Array.isArray(form.variants) ? form.variants : [];

  if (!variants.length) return "Add at least one buying option.";

  const seen = new Set();

  for (const variant of variants) {
    const variantType = normalizeVariantType(variant.variantType);

    if (!TEXTILE_VARIANT_TYPE_SET.has(variantType)) {
      return "Choose Sample, Half Metre or One Metre. Existing legacy Cut records may be retained.";
    }

    if (seen.has(variantType)) {
      return "Each textile buying option can only be used once.";
    }

    seen.add(variantType);

    if (!hasPositiveNumber(variant.price)) {
      return `Price is required for ${variantType}.`;
    }
  }

  if (
    toNumberOrZero(form.lengthReserved) > toNumberOrZero(form.lengthInStock)
  ) {
    return "Length reserved cannot exceed length in stock.";
  }

  if (
    toIntegerOrZero(form.sampleReserved) > toIntegerOrZero(form.sampleInStock)
  ) {
    return "Sample reserved cannot exceed sample stock.";
  }

  return "";
}

/* -------------------------------------------------------------------------- */
/*                                  Payload                                   */
/* -------------------------------------------------------------------------- */

export function buildPayload(form) {
  const colors = splitTokens(form.colorsText);
  const materials = splitTokens(form.materialsText);
  const patterns = splitTokens(form.patternsText);
  const tags = splitTokens(form.tagsText);
  const groups = splitTokens(form.groupsText);

  const unitType = normalizeVariantType(form.unitType);

  const flags = FLAG_FIELDS.reduce((acc, key) => {
    acc[key] = Boolean(form[key]);
    return acc;
  }, {});

  const payload = {
    shopKey: safeText(form.shopKey) || SHOP_KEY,

    category: safeText(form.category),
    subCategory: safeText(form.subcategory) || null,

    name: safeText(form.name),
    brand: safeText(form.brand),
    description: safeText(form.description),
    sku: normalizeSku(form.sku),

    isVariant: Boolean(form.isVariant),

    images: normalizeImageArray(form.images),

    color: colors[0] || "",
    colors,

    material: materials[0] || "",
    materials,

    pattern: patterns[0] || "",
    patterns,

    size: "",
    sizes: [],

    tags,
    groups,

    origin: safeText(form.origin),
    usage: safeText(form.usage),
    careInstructions: safeText(form.careInstructions),

    ...flags,

    madeToMeasure: {
      eligibleAsDesign: false,
      eligibleAsTextile: Boolean(form.madeToMeasureEligible),
      eligibleAsHaberdashery: false,
      suitabilityNotes: safeText(form.madeToMeasureSuitabilityNotes),
    },

    textile: {
      /*
       * Sell mode controls the available buying UI.
       * Inventory pools remain independent. Do not infer sample stock from
       * unitType, and do not zero length stock when the simple option is sample.
       */
      sellMode: form.isVariant ? "variant" : "unit",

      unitType: form.isVariant ? DEFAULT_VARIANT_TYPE : unitType,
      unitValue: form.isVariant ? 1 : unitType === "cut" ? toNumberOrZero(form.unitValue) : getVariantUnitValue(unitType),
      enabled: form.enabled !== false && (form.isVariant || unitType !== "cut"),
      isActive: form.enabled !== false && (form.isVariant || unitType !== "cut"),

      lengthInStock: toNumberOrZero(form.lengthInStock),
      lengthReserved: toNumberOrZero(form.lengthReserved),

      sampleInStock: toIntegerOrZero(form.sampleInStock),
      sampleReserved: toIntegerOrZero(form.sampleReserved),
      sampleLimit: toIntegerOrZero(form.sampleLimit),
    },
  };

  if (form.isVariant) {
    payload.price = 0;
    payload.countInStock = 0;
    payload.countReserved = 0;

    payload.variants = form.variants.map((variant) => {
      const variantType = normalizeVariantType(variant.variantType);

      return {
        variantType,
        price: toNumberOrZero(variant.price),
        unitValue: variantType === "cut" ? toNumberOrZero(variant.legacyUnitValue) : getVariantUnitValue(variantType),
        enabled: variant.enabled !== false && variantType !== "cut",
        isActive: variant.enabled !== false && variantType !== "cut",
        unitLabel: "",
      };
    });

    return payload;
  }

  payload.price = toNumberOrZero(form.price);
  payload.countInStock = 0;
  payload.countReserved = 0;
  payload.variants = [];

  return payload;
}

/* -------------------------------------------------------------------------- */
/*                                  UI atoms                                  */
/* -------------------------------------------------------------------------- */

export const Field = memo(function Field({
  label,
  children,
  hint = "",
  error = "",
}) {
  return (
    <label className="admin-field">
      {label ? <span className="admin-field__label">{label}</span> : null}

      {children}

      {hint ? <span className="admin-field__hint">{hint}</span> : null}

      {error ? <span className="admin-field__error">{error}</span> : null}
    </label>
  );
});

export const Section = memo(function Section({ title, hint = "", children }) {
  return (
    <section className="admin-form-section">
      {title || hint ? (
        <div className="admin-form-section__header">
          {title ? (
            <h3 className="admin-form-section__title">{title}</h3>
          ) : null}

          {hint ? <p className="admin-form-section__hint">{hint}</p> : null}
        </div>
      ) : null}

      {children}
    </section>
  );
});
